import { CommonModule } from '@angular/common';
import { Component, effect, ElementRef, inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { StreamCreditService } from 'src/app/services/api/projects/stream-credit.service';
import { MediasoupService } from 'src/app/services/core/mediasoup/mediasoup.service';
import { ButtonComponent } from 'ui-library';
import { SafePipe } from "../../../../../pipes/safe.pipe";
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';

// The id of a YouTube video, in its embed, watch or short link.
export function youtubeVideoId(url?: string | null): string | null {
  const match = /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?(?:.*&)?v=|shorts\/)|youtu\.be\/)([\w-]{11})/.exec(url ?? '');
  return match ? match[1]! : null;
}

@Component({
  selector: 'app-video',
  imports: [
    WidgetTitleComponent,
    CommonModule,
    SafePipe,
    ButtonComponent,
    TranslateModule
  ],
  standalone: true,
  templateUrl: './video.component.html',
  styleUrl: './video.component.scss'
})
export class VideoComponent extends WidgetDirective {
  // Services specific to VideoComponent
  private mediasoup: MediasoupService = inject(MediasoupService);
  private dialog: MatDialog = inject(MatDialog);
  private creditService: StreamCreditService = inject(StreamCreditService);

  // Component API specific for VideoComponent
  @ViewChild('remoteVideo', { static: false }) remoteVideo!: ElementRef;

  containerStyle: string = "";
  blockStyle: string = "width:100%;display:flex;flex-direction: column;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  titlePosition: string = "top";
  ready = false;
  started = false;
  playing = false;
  // Set when a play attempt gets no frames (no producer / camera not started).
  noStream = false;
  // "Stream live" is detected from actual frames flowing (covers cold start AND
  // resume, where play() doesn't re-resolve and the producer may have been paused).
  private frameWatch?: ReturnType<typeof setInterval>;
  private frameBaseline = 0;
  private frameWaitedMs = 0;
  private readonly NO_STREAM_MS = 10000; // ~count to 9, then show "No stream" if still no track
  // Grace before metering starts, so the camera's polling start-up isn't billed.
  private meterGraceTimer?: ReturnType<typeof setTimeout>;
  private readonly METER_GRACE_MS = 3000;
  private statusSub?: Subscription;
  // Set while binding srcObject / calling play() so the resulting pause/play
  // DOM events don't drive resume/pauseConsumer (only user actions should).
  private suppressMediaEvents = false;

  // Whether this widget is currently counted as an active stream consumer.
  private consuming = false;
  private consumingKey?: string;

  // The stream is only consumed/metered while visible: tab focused + on-screen.
  private docVisible = !document.hidden;
  private elementVisible = true;
  private io?: IntersectionObserver;
  private readonly onDocVisibility = () => { this.docVisible = !document.hidden; this.applyVisibility(); };
  private get streamVisible(): boolean { return this.docVisible && this.elementVisible; }

  // Pause + block this widget the moment this broadcaster's daily credit runs out.
  private limitEffect = effect(() => {
    if (this.video?.broadcaster && this.creditService.limitReachedFor(this.video.broadcaster) && this.playing) this.enforceLimit();
  });

  // With no daily limit, start playing as soon as today's credit is known and the widget is visible.
  private autoStartEffect = effect(() => {
    if (this.creditService.limitKnown && this.creditService.total === 0) this.autoStart();
  });

  get streamLimitReached(): boolean {
    return !!this.video?.broadcaster && this.creditService.limitReachedFor(this.video.broadcaster);
  }

  /** "mm:ss / mm:ss" (played / allowed) for the overlay and badge; "" if unknown. */
  get streamUsageLabel(): string {
    const c = this.video?.broadcaster ? this.creditService.creditFor(this.video.broadcaster) : null;
    if (!c) return '';
    return c.total > 0 ? `${this.toClock(c.played)} / ${this.toClock(c.total)}` : this.toClock(c.played);
  }

  private toClock(seconds: number): string {
    const s = Math.floor(seconds);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  }

  get streamLimitDuration(): string {
    const total = this.creditService.total;
    if (total <= 0) return '';
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return h > 0 ? `${h}h${m > 0 ? m + 'min' : ''}` : `${m}min`;
  }

  // Video specific properties
  video: any;
  // YouTube: the player is loaded on the first play (see youtubeId).
  youtubeStarted: boolean = false;

  get youtubeId(): string | null {
    return youtubeVideoId(this.video?.url);
  }

  // Started by a click: plays at once.
  get youtubeSrc(): string {
    const url: string = this.video?.url ?? '';
    if (!this.youtubeStarted || /[?&]autoplay=/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'autoplay=1';
  }
  image: any;
  playPromise: any;
  videoTrack?: MediaStreamTrack;
  imageStyle: string = "100%";
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    setTimeout(() => this.updateVideo(), Math.random() * 100 + 200);
  }

  resetStyles() {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.containerStyle = "";
  }

  initOptions() {
    try {
      this.resetStyles();
      let options = this.widgetDesign;

      if (!options) return

      this.containerStyle = `background-color: var(--background-color);`
      if (options.title) {
        this.titlePosition = options.title.position || 'top';
        this.showTitle = options.title.display == undefined ? true : options.title.display;
        this.titleStyle = `font-size:${options.title.fontSize || '16'}px; margin:15px 20px 15px;`;
        this.blockStyle += `height:100%;color: var(--text-color); text-align:${options.title.align || 'center'};`;
        this.imageStyle = options.imageFormat.displayMode == 'Stretched' ? '100%' : 'auto';
      }
    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  updateVideo() {
    // Only resolve the video configuration here. Consuming the mediastream is
    // deferred until the user clicks the play button (startStream()).
    if (!this.video && this.widgetDesign && this.widgetDesign.video) {
      this.video = this.widgetDesign.video;
    }

    // The project (and its stream limit) is cached client-side, so re-fetch
    // today's credit from the backend whenever a video widget is shown.
    if (this.video?.type === 'mediastream') {
      this.creditService.refresh();
      this.setupVisibility(); // monitor visibility now so an unlimited stream can auto-start
    }

    // YouTube: rendered once its thumbnail is loaded.
    if (this.video?.type === 'youtube' && this.youtubeId) {
      const thumbnail = new Image();
      thumbnail.onload = thumbnail.onerror = () => {
        this.widgetRendered = true;
        this.cdr.markForCheck();
      };
      thumbnail.src = `https://i.ytimg.com/vi/${this.youtubeId}/hqdefault.jpg`;
    } else {
      this.widgetRendered = true;
    }
  }

  /**
   * Connect to the broadcaster's room (if needed) and start consuming the stream.
   * Triggered by the play button so consumption never starts automatically.
   */
  startStream() {
    if (this.started && !this.noStream) return; // allow retry from the "no stream" state
    if (!this.video || this.video.type !== 'mediastream') return;
    if (this.streamLimitReached) return; // daily playback credit exhausted

    this.started = true;
    this.playing = true;
    // Mark as watched now so its consumer auto-resumes as soon as it arrives.
    this.mediasoup.resumeVideoConsumer(this.video.broadcaster);
    this.setupVisibility();
    this.beginPlayAttempt(); // show the loading count-up until real frames flow

    // Play once a consumer for this broadcaster is (or becomes) available.
    this.statusSub?.unsubscribe();
    this.statusSub = this.mediasoup.status.subscribe(async (status: { type?: string; peerId: string; producerId: any; broadcasterId: any; }) => {
      try {
        // Stream actually gone (broadcaster stopped): stop metering and clear the
        // frame. Keep the user's play intent and wait (loading) for it to return.
        if (status.type === 'producer-ended' && status.broadcasterId === this.video.broadcaster) {
          this.stopConsuming();
          const el = this.remoteVideo?.nativeElement;
          if (el) el.srcObject = null;
          if (this.playing) this.beginPlayAttempt();
          return;
        }

        if (status.producerId && status.broadcasterId == this.video.broadcaster) {
          if (!this.playing) {
            // User had paused; a new consumer (e.g. producer restarted) is
            // auto-resumed server-side, so re-pause it instead of playing.
            this.mediasoup.pauseVideoConsumer(this.video.broadcaster);
            return;
          }
          this.playVideo();
          this.beginPlayAttempt();
        }
      } catch (e) {
        console.error("Error playing video:", e);
      }
    });

    if (this.mediasoup.getRoom() === this.video.room) {
      // Already connected; resume + play the existing track if one is available.
      this.mediasoup.resumeVideoConsumer(this.video.broadcaster);
      this.playVideo();
    } else {
      // First time in this room — connect; the server pushes consumers on join.
      let peerId = Math.floor(Math.random() * 1000000).toString();
      this.videoTrack = undefined;
      this.mediasoup.connect(peerId, this.video.room);
    }
  }

  /** Bind the broadcaster's current consumer track to the <video> element. */
  private playVideo() {
    if (!this.video || !this.remoteVideo?.nativeElement) return;

    try {
      this.videoTrack = this.mediasoup.consumeVideoTrack(this.video.broadcaster);
    } catch {
      this.videoTrack = undefined;
    }

    const videoElem = this.remoteVideo.nativeElement;

    if (!this.videoTrack) {
      videoElem.srcObject = null;
      return;
    }

    // Already bound to this track — rebinding fires spurious pause/play events
    // that would toggle the consumer gate.
    const current = videoElem.srcObject as MediaStream | null;
    if (current && current.getVideoTracks()[0] === this.videoTrack) return;

    // The stream is gone once the consumer track ends — stop metering so usage
    // doesn't keep counting a stream that's no longer there.
    this.videoTrack.onended = () => this.stopConsuming();

    this.suppressMediaEvents = true;
    videoElem.srcObject = new MediaStream([this.videoTrack]);
    videoElem.muted = true;
    videoElem.playsInline = true;

    videoElem.onloadedmetadata = () => {
      const playPromise = videoElem.play();
      const release = () => setTimeout(() => this.suppressMediaEvents = false, 0);

      if (playPromise !== undefined) {
        playPromise.then(() => {
          // "Live" is normally detected from frames flowing (frame watch). If the
          // browser lacks frame stats, fall back to "play() resolved == live".
          if (typeof videoElem.getVideoPlaybackQuality !== 'function') this.onStreamLive();
        }).catch((error: any) => {
          console.warn('Autoplay bloqué, affichage des contrôles:', error);
          videoElem.controls = true;
          videoElem.muted = false;
        }).finally(release);
      } else {
        release();
      }
    };
  }

  /** First click on the (empty) video element starts consuming. */
  onVideoClick() {
    if (this.streamLimitReached) return; // no interaction once the daily credit is used up
    if (!this.started) this.startStream();
  }

  /** Native play (after a pause) resumes the consumer. */
  onVideoPlay() {
    if (this.suppressMediaEvents || !this.streamVisible) return; // ignore non-user / background events
    if (this.streamLimitReached) return; // daily playback credit exhausted
    if (this.started && !this.playing && this.video?.broadcaster) {
      this.playing = true;
      this.mediasoup.resumeVideoConsumer(this.video.broadcaster);
      // Rebind in case the producer restarted while paused (track changed).
      this.playVideo();
      // Show loading again until frames actually resume (the producer may have
      // been gate-paused and needs the server's polling to start it back up).
      this.beginPlayAttempt();
    }
  }

  /** Native pause stops the consumer (server stops sending RTP); resumable. */
  onVideoPause() {
    if (this.suppressMediaEvents || !this.streamVisible) return; // ignore background auto-pause
    if (this.started && this.playing && this.video?.broadcaster) {
      this.playing = false;
      this.stopFrameWatch();
      this.stopConsuming();
      this.mediasoup.pauseVideoConsumer(this.video.broadcaster);
    }
  }

  /** Mark this widget as an active stream consumer (drives the credit meter). */
  private startConsuming() {
    const key: string = this.video?.broadcaster;
    if (this.consuming || !key) return;
    this.consuming = true;
    this.consumingKey = key;
    this.creditService.startConsuming(key);
  }

  /** Stop counting this widget toward the credit meter. */
  private stopConsuming() {
    if (this.meterGraceTimer) { clearTimeout(this.meterGraceTimer); this.meterGraceTimer = undefined; }
    if (!this.consuming) return;
    this.consuming = false;
    this.creditService.stopConsuming(this.consumingKey!);
    this.consumingKey = undefined;
  }

  /** Start metering after a short grace, so the camera's polling start-up isn't billed. */
  private scheduleMetering() {
    if (this.consuming || this.meterGraceTimer) return;
    this.meterGraceTimer = setTimeout(() => {
      this.meterGraceTimer = undefined;
      if (this.playing && this.streamVisible && this.ready && !this.streamLimitReached) this.startConsuming();
    }, this.METER_GRACE_MS);
  }

  /** Begin a play attempt: show the loading count-up and watch for real frames. */
  private beginPlayAttempt() {
    this.ready = false;
    this.noStream = false;
    this.startFrameWatch();
  }

  /**
   * Watch for the stream going live (frames flowing). Keeps running even after
   * showing "No stream" so a late-starting producer clears the message. "No
   * stream" is only shown when there is genuinely no track to consume.
   */
  private startFrameWatch() {
    this.stopFrameWatch();
    this.frameBaseline = this.frameCount();
    this.frameWaitedMs = 0;
    this.frameWatch = setInterval(() => {
      if (this.frameCount() > this.frameBaseline) { this.onStreamLive(); return; }
      this.frameWaitedMs += 500;
      // Only declare "No stream" when there's no track at all; if a track exists
      // but isn't rendering yet, keep waiting (don't cover a stream that's coming).
      if (this.frameWaitedMs >= this.NO_STREAM_MS && !this.videoTrack && !this.noStream) {
        this.noStream = true;
      }
    }, 500);
  }

  private stopFrameWatch() {
    if (this.frameWatch) { clearInterval(this.frameWatch); this.frameWatch = undefined; }
  }

  /** Stream is live (frames flowing or `playing` fired): clear loading/no-stream, meter. */
  onStreamLive() {
    if (!this.started || !this.playing) return; // ignore stray events when not watching
    this.stopFrameWatch();
    this.ready = true;
    this.noStream = false;
    if (this.streamVisible) this.scheduleMetering();
  }

  private frameCount(): number {
    const el = this.remoteVideo?.nativeElement;
    try { return el?.getVideoPlaybackQuality?.().totalVideoFrames ?? 0; } catch { return 0; }
  }

  /** Watch tab focus + on-screen state so we only consume a visible stream. */
  private setupVisibility() {
    if (this.io) return;
    if (!this.remoteVideo?.nativeElement) {
      setTimeout(() => this.setupVisibility(), 200); // element not rendered yet
      return;
    }
    this.io = new IntersectionObserver(
      (entries) => {
        this.elementVisible = entries.some(e => e.isIntersecting);
        this.applyVisibility();
      },
      { threshold: 0.1 }
    );
    this.io.observe(this.remoteVideo.nativeElement);
    document.addEventListener('visibilitychange', this.onDocVisibility);
  }

  /** With no daily limit configured, begin playing once the visible widget knows its (zero) credit. */
  private autoStart() {
    if (this.started || !this.video || this.video.type !== 'mediastream' || !this.video.broadcaster) return;
    if (!this.creditService.limitKnown || this.creditService.total > 0) return;
    if (!this.streamVisible) return;
    this.startStream();
  }

  /** Pause/resume consumption to match visibility (keeps the user's play intent). */
  private applyVisibility() {
    if (!this.video?.broadcaster) return;
    if (!this.started) { this.autoStart(); return; }

    if (this.streamVisible) {
      if (this.playing && !this.streamLimitReached && !this.consuming) {
        this.mediasoup.resumeVideoConsumer(this.video.broadcaster);
        this.playVideo();
        this.beginPlayAttempt(); // loading until frames resume
      }
    } else {
      this.stopFrameWatch();
      if (this.consuming) this.stopConsuming();
      this.mediasoup.pauseVideoConsumer(this.video.broadcaster);
    }
  }

  /** Daily credit exhausted: full stop — pause the consumer, stop metering, and
   *  drop the media so no last frame lingers and the element can't buffer/spin. */
  private enforceLimit() {
    this.stopConsuming();
    this.stopFrameWatch();
    this.playing = false;
    this.ready = false;
    if (this.video?.broadcaster) this.mediasoup.pauseVideoConsumer(this.video.broadcaster);
    const el = this.remoteVideo?.nativeElement;
    if (el) { try { el.pause(); } catch {} el.srcObject = null; }
  }

  override ngOnDestroy(): void {
    this.io?.disconnect();
    document.removeEventListener('visibilitychange', this.onDocVisibility);
    this.stopFrameWatch();
    this.stopConsuming();
    this.statusSub?.unsubscribe();
    super.ngOnDestroy();
  }

  onImgClick() {
    const getImageInfo = () => {
      return {
        index: 0,
        id: this.image.id,
        name: this.image.name,
        tracker: this.image.tracker,
        url: this.image.url,
        boxes: this.image.predictions,
        startDate: this.image.createdDate,
        disableArrow: true,
        processed: this.image.processed,
        tagDisplayConfig: this.tagDisplayConfig,
        getImageInfo
      }

    }

    this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: getImageInfo()
    });
  }
}
